// Safe client-side error logging.
//
// NEVER log a raw Axios/Error object to the console. An AxiosError retains `.config`
// (including `config.headers.Authorization`, the participant's Bearer JWT) and `.request`,
// so `console.error('...', error)` can expose a participant-linked credential in the browser
// console — a real privacy leak on a minor's device (Codex deploy-gate finding, 2026-07-15).
//
// Use `logClientError(label, error)` in place of `console.error(label, error)`: it logs the
// label plus a sanitized status/code/message only — never the config, headers, request, or the
// raw object.

/**
 * Reduce any thrown value to a short, credential-free descriptor.
 * Prefers the HTTP status, then a network/code marker, then a bounded message string.
 * Never reads `error.config` / `error.request` / headers.
 * @param {*} error
 * @returns {string}
 */
export function describeClientError(error) {
  if (error == null) return 'unknown error';
  const status = error?.response?.status;
  if (status != null) return `status ${status}`;
  if (error?.code) return String(error.code);
  const message = error?.message;
  if (typeof message === 'string' && message) return message.slice(0, 120);
  return 'error';
}

/**
 * Console-log a fixed label + a sanitized descriptor. Drop-in for `console.error(label, error)`
 * that cannot leak the JWT/headers carried by an AxiosError.
 * @param {string} label
 * @param {*} error
 */
export function logClientError(label, error) {
  // eslint-disable-next-line no-console
  console.error(label, describeClientError(error));
}

/**
 * Warn-level sibling of `logClientError`, for non-fatal failures. Same sanitization.
 * @param {string} label
 * @param {*} error
 */
export function logClientWarn(label, error) {
  // eslint-disable-next-line no-console
  console.warn(label, describeClientError(error));
}
